import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import styles from './OssMp3Combine.module.css';

function normalizeChapter(item) {
  if (!item) {
    return null;
  }
  const missing =
    Array.isArray(item.missing_files)
      ? item.missing_files
      : Array.isArray(item.missingFiles)
        ? item.missingFiles
        : [];
  const hasRequired =
    typeof item.has_required_files === 'boolean'
      ? item.has_required_files
      : Boolean(item.hasRequiredFiles);
  return {
    name: String(item.name ?? item.prefix ?? ''),
    prefix: String(item.prefix ?? ''),
    hasRequiredFiles: hasRequired,
    missingFiles: missing.map((entry) => String(entry)),
  };
}

function OssMp3Combine() {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [isCombining, setIsCombining] = useState(false);
  const [combineError, setCombineError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [result, setResult] = useState(null);
  const [pauseBetweenSegments, setPauseBetweenSegments] = useState(150);
  const [pauseBetweenPairs, setPauseBetweenPairs] = useState(300);
  const [searchTerm, setSearchTerm] = useState('');

  const backendBaseUrl = useMemo(() => {
    const baseUrl = api.defaults.baseURL || '';
    if (!baseUrl) {
      return '';
    }
    const trimmed = baseUrl.replace(/\/+$/, '');
    return trimmed.replace(/\/api$/, '');
  }, []);

  const resolveStaticUrl = useCallback(
    (url) => {
      if (!url) {
        return '';
      }
      if (/^https?:\/\//i.test(url)) {
        return url;
      }
      if (backendBaseUrl) {
        return `${backendBaseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
      }
      return url;
    },
    [backendBaseUrl],
  );

  const loadChapters = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/oss-mp3/chapters', { params: { limit: 500 } });
      const list = Array.isArray(data?.chapters) ? data.chapters : [];
      const normalized = list
        .map(normalizeChapter)
        .filter(Boolean)
        .sort((a, b) => {
          const left = Number.parseInt(a.name, 10);
          const right = Number.parseInt(b.name, 10);
          if (Number.isFinite(left) && Number.isFinite(right)) {
            return left - right;
          }
          return a.name.localeCompare(b.name);
        });
      setChapters(normalized);
      setSelectedChapter((prev) => {
        if (prev && normalized.some((item) => item.prefix === prev.prefix)) {
          return normalized.find((item) => item.prefix === prev.prefix) ?? null;
        }
        return normalized.length > 0 ? normalized[0] : null;
      });
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        '获取 OSS 章节列表失败，请稍后重试';
      setError(message);
      setChapters([]);
      setSelectedChapter(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChapters();
  }, [loadChapters]);

  const filteredChapters = useMemo(() => {
    if (!searchTerm.trim()) {
      return chapters;
    }
    const keyword = searchTerm.trim().toLowerCase();
    return chapters.filter((item) => {
      return (
        item.name.toLowerCase().includes(keyword) ||
        item.prefix.toLowerCase().includes(keyword)
      );
    });
  }, [chapters, searchTerm]);

  const handleSelectChapter = (chapter) => {
    setSelectedChapter(chapter);
    setCombineError('');
    setSuccessMessage('');
    setResult(null);
  };

  const handleCombine = async () => {
    if (!selectedChapter) {
      setCombineError('请先选择章节');
      return;
    }
    if (!selectedChapter.hasRequiredFiles) {
      setCombineError('当前章节缺少必要的四个 OSS 文件，无法生成混合音频');
      return;
    }

    setCombineError('');
    setSuccessMessage('');
    setResult(null);
    setIsCombining(true);

    const chapterIdNumber = Number.parseInt(selectedChapter.name, 10);
    const payload = {
      oss_prefix: selectedChapter.prefix,
      pause_between_segments_ms: Number.parseInt(pauseBetweenSegments, 10) || 0,
      pause_between_pairs_ms: Number.parseInt(pauseBetweenPairs, 10) || 0,
      overwrite: true,
    };
    if (Number.isFinite(chapterIdNumber) && chapterIdNumber > 0) {
      payload.chapter_id = chapterIdNumber;
    }

    try {
      const { data } = await api.post('/oss-mp3/combine', payload);
      setResult(data);
      setSuccessMessage('混合音频与字幕生成成功，已写入本地 static 目录');
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        '生成混合音频失败，请稍后重试';
      setCombineError(message);
    } finally {
      setIsCombining(false);
    }
  };

  const renderStatus = (chapter) => {
    if (!chapter?.hasRequiredFiles) {
      return (
        <span className={`${styles.statusTag} ${styles.statusWarn}`}>
          缺少文件
        </span>
      );
    }
    return (
      <span className={`${styles.statusTag} ${styles.statusOk}`}>
        可生成
      </span>
    );
  };

  const renderResultLinks = (variant, label) => {
    if (!variant) {
      return null;
    }
    return (
      <div className={styles.resultSection}>
        <strong>{label}</strong>
        <div className={styles.linkRow}>
          <span>音频：</span>
          <a href={resolveStaticUrl(variant.audio?.url)} target="_blank" rel="noreferrer">
            {variant.audio?.path}
          </a>
        </div>
        <div className={styles.linkRow}>
          <span>字幕：</span>
          <a href={resolveStaticUrl(variant.srt?.url)} target="_blank" rel="noreferrer">
            {variant.srt?.path}
          </a>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <div>
            <h2>OSS 混合音频生成</h2>
            <p className={styles.subtitle}>
              自动从 OSS 下载 en/cn 字幕与音频，合成交错语音并保存到服务器本地
            </p>
          </div>
          <div className={styles.actions}>
            <input
              className={styles.searchInput}
              placeholder="搜索章节 ID 或前缀..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            <button
              type="button"
              className={styles.refreshButton}
              onClick={loadChapters}
              disabled={loading}
            >
              {loading ? '刷新中...' : '刷新列表'}
            </button>
          </div>
        </div>
        {error && <div className={styles.error}>{error}</div>}
      </div>

      <div className={styles.layout}>
        <div className={styles.panel}>
          <h3>章节列表</h3>
          <div className={styles.chapterList}>
            {filteredChapters.length === 0 && !loading && (
              <div>暂无可用章节</div>
            )}
            {filteredChapters.map((chapter) => {
              const isSelected = selectedChapter?.prefix === chapter.prefix;
              return (
                <button
                  key={chapter.prefix}
                  type="button"
                  className={`${styles.chapterItem} ${
                    isSelected ? styles.chapterItemSelected : ''
                  }`}
                  onClick={() => handleSelectChapter(chapter)}
                >
                  <span className={styles.chapterName}>章节 {chapter.name}</span>
                  <span className={styles.chapterPrefix}>{chapter.prefix}</span>
                  {renderStatus(chapter)}
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.panel}>
          <h3>生成设置</h3>
          {selectedChapter ? (
            <div className={styles.details}>
              <div className={styles.detailRow}>
                <label>章节编号</label>
                <span>{selectedChapter.name}</span>
              </div>
              <div className={styles.detailRow}>
                <label>OSS 前缀</label>
                <span>{selectedChapter.prefix}</span>
              </div>
              {!selectedChapter.hasRequiredFiles && (
                <div className={styles.detailRow}>
                  <label>缺失文件</label>
                  <div className={styles.missingList}>
                    {selectedChapter.missingFiles.length === 0
                      ? '未知'
                      : selectedChapter.missingFiles.map((file) => (
                          <span key={file} className={styles.missingItem}>
                            {file}
                          </span>
                        ))}
                  </div>
                </div>
              )}
              <div className={styles.detailRow}>
                <label>句内静默(ms)</label>
                <input
                  type="number"
                  className={styles.input}
                  value={pauseBetweenSegments}
                  min="0"
                  onChange={(event) => setPauseBetweenSegments(event.target.value)}
                />
              </div>
              <div className={styles.detailRow}>
                <label>句间静默(ms)</label>
                <input
                  type="number"
                  className={styles.input}
                  value={pauseBetweenPairs}
                  min="0"
                  onChange={(event) => setPauseBetweenPairs(event.target.value)}
                />
              </div>
              <button
                type="button"
                className={styles.combineButton}
                onClick={handleCombine}
                disabled={isCombining}
              >
                {isCombining ? '生成中...' : '生成混合音频'}
              </button>

              {combineError && <div className={styles.error}>{combineError}</div>}
              {successMessage && <div className={styles.success}>{successMessage}</div>}

              {result && (
                <div className={styles.resultCard}>
                  <div>
                    已处理句子：EN {result.en_sentence_count} / CN {result.cn_sentence_count}
                  </div>
                  {renderResultLinks(result.en_cn, '英文优先 (en_cn)')}
                  {renderResultLinks(result.cn_en, '中文优先 (cn_en)')}
                </div>
              )}
            </div>
          ) : (
            <div>请选择章节查看详情</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OssMp3Combine;
